import React, { type FC, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Drawer,
    DrawerBody,
    DrawerCloseButton,
    DrawerContent,
    DrawerHeader,
    DrawerOverlay,
    Flex,
    Input,
    Spinner,
    Text,
    useDisclosure,
} from '@chakra-ui/react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useCustomerClearContext } from '../../bff-api/customers/useCustomerClearContext';
import { useCustomerContextFixer } from '../../bff-api/customers/useCustomerContextFixer';
import { useCustomerContextGet } from '../../bff-api/customers/useCustomerContextGet';
import { useCustomerUpdateContext } from '../../bff-api/customers/useCustomerUpdateContext';
import { useEffectiveDateTime } from '../../bff-api/customers/useEffectiveDateTime';
import { StorageKey } from '../../hooks/model/StorageKey';
import { useLocalStoragePubSub } from '../../hooks/useLocalStoragePubSub';
import { Button } from '../../ui-components';
import { IconButton } from '../../ui-components/button/icon-button';
import { useCustomPreferenceOrDefault } from '../../utils/api';
import { CustomPreference } from '../../utils/api/CustomPreference';
import {
    cleanEffectiveDateTime,
    normalizeEffectiveDateTime,
    setPreviewDateInUrl,
    toLocalDateTimeString,
} from '../../utils/preview-mode-utils';

export const EffectiveDateTimeManager: FC = () => {
    const pwaSurlatablePreview = useCustomPreferenceOrDefault(
        CustomPreference.PWA_SURLATABLE_PREVIEW,
        false,
    );
    const currentEffectiveDateTime = useEffectiveDateTime();
    const intl = useIntl();
    const contextQuery = useCustomerContextGet();
    const updateMutation = useCustomerUpdateContext();
    const clearMutation = useCustomerClearContext();
    useCustomerContextFixer();
    const { isOpen, onOpen, onClose } = useDisclosure();

    const [dateTimeValue, setDateTimeValue] = useState(() => {
        const date = currentEffectiveDateTime ? new Date(currentEffectiveDateTime) : null;
        if (!date) {
            return '';
        }
        return toLocalDateTimeString(date);
    });

    const changedDateTimeValue = useMemo(
        () =>
            cleanEffectiveDateTime(dateTimeValue) !==
            cleanEffectiveDateTime(currentEffectiveDateTime),
        [dateTimeValue, currentEffectiveDateTime],
    );

    const handlePreview = async (e: React.FormEvent): Promise<void> => {
        e.preventDefault();

        if (!dateTimeValue) {
            return;
        }

        try {
            const effectiveDateTime = dateTimeValue
                ? // the context API expects the date time string in ISO 8601 format without milliseconds (Z)
                  normalizeEffectiveDateTime(dateTimeValue)
                : undefined;
            await updateMutation.mutateAsync({
                effectiveDateTime,
            });
        } catch (error) {
            // Error will be handled by mutation state
            console.error('Failed to update effective date time:', error);
        }
    };

    const handleClear = async (): Promise<void> => {
        try {
            await clearMutation.mutateAsync(undefined);
            setDateTimeValue('');
        } catch (error) {
            // Error will be handled by mutation state

            console.error('Failed to clear effective date time:', error);
        }
    };

    useLocalStoragePubSub(StorageKey.PREVIEW_MODE_DATE, (value: string): void => {
        setPreviewDateInUrl(value ?? '');
    });

    const { isLoading } = contextQuery;

    if (!pwaSurlatablePreview) {
        return null;
    }

    return (
        <>
            <Box
                position="fixed"
                top={10}
                left={1}
                zIndex={1000}
                borderRadius="full"
                backgroundColor="slt-pepper.100"
                boxShadow="lg"
                className="print:hidden"
            >
                <IconButton
                    id="effective-date-time-manager-open-button"
                    aria-label="Open Preview Mode"
                    iconName="navigation-arrow-right"
                    onClick={onOpen}
                    data-testid="effective-date-time-manager-open-button"
                    size="md"
                    isLoading={isLoading || updateMutation.isLoading || clearMutation.isLoading}
                    isDisabled={isLoading || updateMutation.isLoading || clearMutation.isLoading}
                />
            </Box>

            <Drawer isOpen={isOpen} placement="left" onClose={onClose} size="md">
                <DrawerOverlay />
                {/**
                 * The zIndex is set to 2000 to ensure the drawer is displayed above the open button
                 * and the preview mode button and the header menu dropdown which is z-index=1400.
                 */}
                <DrawerContent data-testid="effective-date-time-manager-drawer" zIndex={2000}>
                    <DrawerCloseButton data-testid="effective-date-time-manager-close-button" />
                    <DrawerHeader borderBottomWidth="1px">
                        <Text fontSize="lg" fontWeight="bold">
                            <FormattedMessage
                                id="effective-date-time-manager.title"
                                defaultMessage="Site Preview"
                            />
                        </Text>
                    </DrawerHeader>

                    <DrawerBody>
                        <Flex
                            direction="column"
                            gap={4}
                            py={4}
                            data-testid="effective-date-time-manager"
                        >
                            {isLoading ? (
                                <Flex align="center" gap={2}>
                                    <Spinner size="sm" />
                                    <Text fontSize="sm" color="gray.600">
                                        <FormattedMessage
                                            id="effective-date-time-manager.loading"
                                            defaultMessage="Loading..."
                                        />
                                    </Text>
                                </Flex>
                            ) : (
                                <>
                                    {currentEffectiveDateTime && (
                                        <Flex direction="column" gap={1}>
                                            <Text fontSize="sm" fontWeight="semibold">
                                                <FormattedMessage
                                                    id="effective-date-time-manager.current-value"
                                                    defaultMessage="Current Effective Date Time:"
                                                />
                                            </Text>
                                            <Text
                                                fontSize="md"
                                                color="blue.600"
                                                data-testid="effective-date-time-manager-current-value"
                                            >
                                                {currentEffectiveDateTime}
                                            </Text>
                                        </Flex>
                                    )}

                                    {updateMutation.isError && (
                                        <Alert
                                            status="error"
                                            borderRadius={4}
                                            data-testid="effective-date-time-manager-error"
                                        >
                                            <Text fontSize="sm" color="red.600">
                                                <FormattedMessage
                                                    id="effective-date-time-manager.error"
                                                    defaultMessage="Failed to update effective date time. Please try again."
                                                />
                                            </Text>
                                        </Alert>
                                    )}

                                    {updateMutation.isSuccess && (
                                        <Alert
                                            status="success"
                                            borderRadius={4}
                                            data-testid="effective-date-time-manager-success"
                                        >
                                            <Text fontSize="sm" color="green.600">
                                                <FormattedMessage
                                                    id="effective-date-time-manager.success"
                                                    defaultMessage="Effective date time updated successfully!"
                                                />
                                            </Text>
                                        </Alert>
                                    )}

                                    <form onSubmit={handlePreview}>
                                        <Flex direction="column" gap={3}>
                                            <Flex direction="column" gap={1}>
                                                <Text
                                                    fontSize="sm"
                                                    fontWeight="semibold"
                                                    as="label"
                                                    htmlFor="datetime-input"
                                                >
                                                    <FormattedMessage
                                                        id="effective-date-time-manager.input-label"
                                                        defaultMessage="New Effective Date Time:"
                                                    />
                                                </Text>
                                                <Input
                                                    id="datetime-input"
                                                    type="datetime-local"
                                                    value={dateTimeValue}
                                                    onChange={(e) =>
                                                        setDateTimeValue(e.target.value)
                                                    }
                                                    placeholder={intl.formatMessage({
                                                        id: 'effective-date-time-manager.input-placeholder',
                                                        defaultMessage: 'Select date and time',
                                                    })}
                                                    isDisabled={updateMutation.isLoading}
                                                    data-testid="effective-date-time-manager-input"
                                                />
                                            </Flex>

                                            <Flex gap={2}>
                                                <Button
                                                    kind="button"
                                                    variant="secondary"
                                                    isDisabled={
                                                        !currentEffectiveDateTime ||
                                                        clearMutation.isLoading ||
                                                        updateMutation.isLoading
                                                    }
                                                    isLoading={clearMutation.isLoading}
                                                    id="effective-date-time-manager-clear-button"
                                                    onClick={handleClear}
                                                    wide
                                                >
                                                    <FormattedMessage
                                                        id="effective-date-time-manager.clear-button"
                                                        defaultMessage="Clear"
                                                    />
                                                </Button>
                                                <Button
                                                    kind="submit"
                                                    variant="primary"
                                                    isDisabled={
                                                        !dateTimeValue ||
                                                        !changedDateTimeValue ||
                                                        clearMutation.isLoading ||
                                                        updateMutation.isLoading
                                                    }
                                                    isLoading={updateMutation.isLoading}
                                                    id="effective-date-time-manager-preview-button"
                                                    wide
                                                >
                                                    <FormattedMessage
                                                        id="effective-date-time-manager.preview-button"
                                                        defaultMessage="Preview"
                                                    />
                                                </Button>
                                            </Flex>
                                        </Flex>
                                    </form>
                                </>
                            )}
                        </Flex>
                    </DrawerBody>
                </DrawerContent>
            </Drawer>
        </>
    );
};
